import type { Difficulty, GameMode } from '../shared/constants';
import type { RoomPlayer, ServerMessage } from '../shared/protocol';
import { connect, type Connection } from './net';
import { escHtml, footerText, lobbyName, lobbyRoom, ownSeat, rememberSeat, seatTokens, setNameRoom } from './session';

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element #${id}`);
  return node as T;
}

const ui = {
  loginForm: el('loginForm'),
  roomPanel: el('roomPanel'),
  btnJoin: el<HTMLButtonElement>('btnJoin'),
  btnStart: el<HTMLButtonElement>('btnStart'),
  btnLeave: el<HTMLButtonElement>('btnLeave'),
  btnAddBot: el<HTMLButtonElement>('btnAddBot'),
  botDifficulty: el<HTMLSelectElement>('botDifficulty'),
  playerName: el<HTMLInputElement>('playerName'),
  roomName: el<HTMLInputElement>('roomName'),
  playerList: el('playerList'),
  roomNameDisplay: el('roomNameDisplay'),
  startHint: el('startHint'),
  lobbyError: el('lobbyError'),
  botControls: el('botControls'),
  startBtnWrapper: el('startBtnWrapper'),
  modeSelect: el('modeSelect'),
  modeOptions: Array.from(document.querySelectorAll<HTMLButtonElement>('.mode-option')),
  roomList: el('roomList'),
  btnRefreshRooms: el<HTMLButtonElement>('btnRefreshRooms'),
  shareControls: el('shareControls'),
  btnShareLink: el<HTMLButtonElement>('btnShareLink'),
  shareFeedback: el('shareFeedback'),
  inviteBanner: el('inviteBanner'),
  inviteRoomName: el('inviteRoomName'),
  roomNameGroup: el('roomNameGroup'),
};

interface OpenRoom { name: string; creator: string; players: number; }
let roomPollTimer: number | null = null;

let conn: Connection | null = null;
let myPlayerId: string | null = null;
let isCreator = false;
let currentRoom: string | null = null;
let selectedMode: GameMode = 'classic';

const DIFF_LABELS: Record<string, string> = { easy: 'Leicht', medium: 'Mittel', hard: 'Schwer' };

function showError(msg: string): void { ui.lobbyError.textContent = msg; ui.lobbyError.hidden = false; }
function clearError(): void { ui.lobbyError.hidden = true; }

// ── Open-room browser ─────────────────────────────────────────────────────────
function renderRooms(list: OpenRoom[]): void {
  if (list.length === 0) {
    ui.roomList.innerHTML = '<p class="room-list-empty">Keine offenen Räume. Gib oben einen Namen ein, um einen neuen zu erstellen.</p>';
    return;
  }
  ui.roomList.innerHTML = list
    .map((r) => `
      <button class="room-item" data-room="${escHtml(r.name)}" style="touch-action:manipulation">
        <span class="room-item-name">${escHtml(r.name)}</span>
        <span class="room-item-meta">👑 ${escHtml(r.creator)} · ${r.players} 👤</span>
      </button>
    `)
    .join('');
}

async function loadRooms(): Promise<void> {
  try {
    const res = await fetch('/api/rooms');
    if (!res.ok) return;
    const data = (await res.json()) as { rooms: OpenRoom[] };
    if (!ui.loginForm.hidden) renderRooms(data.rooms);
  } catch {
    /* offline / server unreachable — leave the current list as-is */
  }
}

function startRoomPolling(): void {
  void loadRooms();
  if (roomPollTimer === null) roomPollTimer = window.setInterval(loadRooms, 5000);
}
function stopRoomPolling(): void {
  if (roomPollTimer !== null) { clearInterval(roomPollTimer); roomPollTimer = null; }
}

function handleMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case 'joined':
      myPlayerId = msg.playerId;
      isCreator = msg.isCreator;
      currentRoom = msg.roomName;
      rememberSeat(msg.roomName, msg.token);
      // Remember name + room as soon as we hold a seat, so closing the tab in
      // the lobby (not just in-game) still leaves a way back.
      setNameRoom(ui.playerName.value.trim(), msg.roomName);
      ui.roomNameDisplay.textContent = msg.roomName;
      ui.loginForm.hidden = true;
      ui.roomPanel.hidden = false;
      ui.startBtnWrapper.hidden = !isCreator;
      ui.botControls.hidden = !isCreator;
      ui.shareControls.hidden = !isCreator;
      stopRoomPolling();
      break;

    case 'room_update':
      // The creator can be re-elected while we sit here (the previous one left
      // or dropped out), so re-derive it on every update instead of trusting
      // the value from `joined` for the rest of the session.
      isCreator = msg.creatorId !== null && msg.creatorId === myPlayerId;
      ui.startBtnWrapper.hidden = !isCreator;
      ui.botControls.hidden = !isCreator;
      ui.shareControls.hidden = !isCreator;
      renderPlayerList(msg.players, msg.creatorId);
      if (isCreator) {
        const total = msg.players.length;
        ui.btnStart.disabled = total < 2;
        ui.startHint.textContent = total < 2
          ? 'Mindestens 2 Teilnehmer (oder 1 Bot) nötig.'
          : `${total} Teilnehmer – bereit!`;
        ui.btnAddBot.disabled = total >= 8;
      }
      if (msg.gameRunning && currentRoom) goToGame();
      break;

    case 'game_started':
      if (currentRoom) goToGame();
      break;

    case 'error':
      showError(msg.message);
      break;
  }
}

/**
 * Hands this tab over to the game page.
 *
 * The lobby socket must be closed *before* game.html joins: a seat is only
 * reclaimable once its connection is gone (see Room.isReclaimable), so a join
 * that overtakes the close is turned away with "Spiel läuft bereits" and the
 * player lands on an empty board. Navigating merely on unload is not enough —
 * the new page can finish its handshake first. So we close, wait for the socket
 * to actually be gone, and only then navigate.
 */
let navigating = false;
let afterSocketClose: (() => void) | null = null;

function goToGame(): void {
  if (navigating || !currentRoom) return;
  navigating = true;
  setNameRoom(ui.playerName.value.trim(), currentRoom);

  let done = false;
  const go = (): void => {
    if (done) return;
    done = true;
    location.href = 'game.html';
  };
  afterSocketClose = go;
  conn?.close();
  // Never let a socket that refuses to close strand the player in the lobby.
  setTimeout(go, 1000);
}

function joinRoom(): void {
  clearError();
  const name = ui.playerName.value.trim();
  const room = ui.roomName.value.trim();
  if (!name) return showError('Bitte gib deinen Namen ein.');
  if (!room) return showError('Bitte gib einen Raumnamen ein.');

  conn = connect({
    onOpen: () => {
      clearError();
      conn?.send({ type: 'join', name, room, token: ownSeat(room), tokens: seatTokens(room) });
    },
    onMessage: handleMessage,
    onClose: () => {
      if (afterSocketClose) return afterSocketClose(); // Übergabe an game.html
      if (!currentRoom) showError('Verbindung getrennt.');
    },
    onError: () => showError('WebSocket-Fehler. Ist der Server erreichbar?'),
  });
}

function renderPlayerList(players: RoomPlayer[], creatorId: string | null): void {
  ui.playerList.innerHTML = players
    .map((p) => {
      const isMe = p.id === myPlayerId;
      const isCreatorP = p.id === creatorId;
      const diffLabel = p.difficulty ? DIFF_LABELS[p.difficulty] ?? '' : '';
      return `
        <div class="player-item ${p.connected ? '' : 'disconnected'}">
          ${p.isBot
            ? `<span class="player-dot bot-dot">🤖</span>`
            : `<span class="player-dot ${p.connected ? 'online' : 'offline'}"></span>`}
          <span class="player-name-txt">${escHtml(p.name)}</span>
          ${p.isBot ? `<span class="diff-badge diff-${p.difficulty}">${diffLabel}</span>` : ''}
          ${isMe ? '<span class="you-badge">Du</span>' : ''}
          ${isCreatorP && !p.isBot ? '<span class="crown-badge">👑</span>' : ''}
          ${p.isBot && isCreator
            ? `<button class="btn-remove-bot" data-bot-id="${escHtml(p.id)}" style="touch-action:manipulation">✕</button>`
            : ''}
        </div>
      `;
    })
    .join('');
}

// Event delegation for the dynamically rendered remove-bot buttons.
ui.playerList.addEventListener('click', (ev) => {
  const target = (ev.target as HTMLElement).closest<HTMLElement>('.btn-remove-bot');
  if (target?.dataset.botId) conn?.send({ type: 'remove_bot', botId: target.dataset.botId });
});

function selectMode(mode: GameMode): void {
  selectedMode = mode;
  for (const opt of ui.modeOptions) {
    const active = opt.dataset.mode === mode;
    opt.classList.toggle('selected', active);
    opt.setAttribute('aria-checked', active ? 'true' : 'false');
  }
}
ui.modeOptions.forEach((opt) => {
  opt.addEventListener('click', () => selectMode((opt.dataset.mode as GameMode) ?? 'classic'));
});

ui.btnJoin.addEventListener('click', joinRoom);
ui.btnStart.addEventListener('click', () => conn?.send({ type: 'start_game', mode: selectedMode }));
ui.btnAddBot.addEventListener('click', () => {
  conn?.send({ type: 'add_bot', difficulty: ui.botDifficulty.value as Difficulty });
});
ui.btnLeave.addEventListener('click', () => {
  conn?.close();
  conn = null;
  ui.loginForm.hidden = false;
  ui.roomPanel.hidden = true;
  ui.shareControls.hidden = true;
  currentRoom = null;
  myPlayerId = null;
  startRoomPolling();
});

// Copy an invite link (origin + ?room=) so others land on a pre-filled lobby.
async function copyInviteLink(): Promise<void> {
  if (!currentRoom) return;
  const link = `${location.origin}/?room=${encodeURIComponent(currentRoom)}`;
  try {
    await navigator.clipboard.writeText(link);
  } catch {
    // Fallback for non-secure contexts / older browsers without the Clipboard API.
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
  ui.shareFeedback.hidden = false;
  window.setTimeout(() => { ui.shareFeedback.hidden = true; }, 2000);
}
ui.btnShareLink.addEventListener('click', () => void copyInviteLink());
[ui.playerName, ui.roomName].forEach((input) => {
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') ui.btnJoin.click(); });
});

ui.btnRefreshRooms.addEventListener('click', () => void loadRooms());

// Pick an open room from the browser: fill the name and join (asks for a player name first if missing).
ui.roomList.addEventListener('click', (ev) => {
  const item = (ev.target as HTMLElement).closest<HTMLElement>('.room-item');
  if (!item?.dataset.room) return;
  ui.roomName.value = item.dataset.room;
  if (ui.playerName.value.trim()) joinRoom();
  else { clearError(); showError('Bitte gib zuerst deinen Namen ein.'); ui.playerName.focus(); }
});

// Footer: app version (injected at build time) + copyright.
el('lobbyFooter').textContent = footerText();

// Auto-fill from a previous session. The room falls back to the last one used
// on this device, so a reopened tab can find its way back into a running game
// (rooms with a game in progress are hidden from the open-room browser).
const savedName = lobbyName();
const savedRoom = lobbyRoom();
if (savedName) ui.playerName.value = savedName;
if (savedRoom) ui.roomName.value = savedRoom;

// Invite link: a ?room= parameter pre-fills and locks the room, so the guest
// only needs to type their name. Takes precedence over the saved room.
const invitedRoom = new URLSearchParams(location.search).get('room')?.trim();
if (invitedRoom) {
  ui.roomName.value = invitedRoom;
  ui.roomNameGroup.hidden = true;       // hide the room field — choice is already made
  ui.inviteRoomName.textContent = invitedRoom;
  ui.inviteBanner.hidden = false;
  ui.playerName.focus();
}

// Start browsing open rooms right away.
startRoomPolling();

// Hide the "Loftjo Action" option if the server has it disabled (ACTION_MODE_ENABLED=false).
void (async () => {
  try {
    const res = await fetch('/api/config');
    if (!res.ok) return;
    const cfg = (await res.json()) as { actionEnabled?: boolean };
    if (cfg.actionEnabled === false) {
      const actionOpt = ui.modeOptions.find((o) => o.dataset.mode === 'action');
      if (actionOpt) actionOpt.hidden = true;
      ui.modeSelect.hidden = true; // only one variant left → no choice to show
      selectMode('classic');
    }
  } catch {
    /* leave the default (action shown) on error */
  }
})();
